/**
 * Which approval cards have just arrived in the thread and have not been shown.
 *
 * ── Why this exists (TKT-1346) ──────────────────────────────────────────────
 *
 * On a phone the approval card draws inside the Mucka Bar's sheet, and only
 * while the sheet is open. A live call survives the sheet being put away on
 * purpose — any bottom tab or "go to" button folds it into an orange banner —
 * and from then on nothing Mucka hangs on the conversation is on screen. So an
 * owner who asked for an email, glanced at Actions or the diary, and came back
 * to "just tap Send email below" was looking at a banner with no button in it.
 * Reproduced on dev on 24 Sep: card drawn with the sheet open, gone the moment
 * a tab was tapped, and nothing ever brought it back.
 *
 * A card is the one thing on that thread the owner is waiting to act on — they
 * asked for it seconds ago — so its arrival brings the sheet back up.
 *
 * `seen` is the caller's memory of every id already considered. Ids present on
 * the first read are NOT news: those are the stored conversation reloading, and
 * reopening a sheet for them would pop it on every page load.
 */
export function newStagedIds(
  seen: Set<string> | null,
  messages: readonly { stagedActions?: readonly { id: string }[] }[],
): { fresh: string[]; seen: Set<string> } {
  const ids = messages.flatMap(m => (m.stagedActions ?? []).map(sa => sa.id));
  if (!seen) return { fresh: [], seen: new Set(ids) };
  const fresh = ids.filter(id => !seen.has(id));
  if (!fresh.length) return { fresh, seen };
  return { fresh: [...new Set(fresh)], seen: new Set([...seen, ...fresh]) };
}

/**
 * Cards from THIS call that the page inherited rather than watched arrive.
 *
 * ── Why this exists (MUCKA-WEB-C9) ──────────────────────────────────────────
 *
 * `newStagedIds` reveals a card on its ARRIVAL during a live call. On the
 * iPhone app the call belongs to the app, not the page, so it carries on
 * through a WebView reload, and the reloaded page meets things in the wrong
 * order for that rule: the thread comes back from sessionStorage first, card
 * included, and the call is only known to be live once the native snapshot
 * has been read. So the card is filed as the stored conversation reloading,
 * and the sheet stays folded for the rest of the call. 8 Oct 2026: a card
 * written 79s before the page loaded, the call live for 47s after, and no
 * Send button on screen at any point.
 *
 * The bar cannot tell that card from a stale one in the stored thread (the
 * reason `newStagedIds` ignores the first read). The panel can: `cards` is
 * `watchableStagedCards` — pending, and made during this call. So the panel
 * asks, once per card per page, for any of those that is in the thread and
 * not yet decided. `revealed` is the caller's memory of cards already asked
 * for, so a card the owner has seen and put away is not pushed back at them.
 */
export function inheritedStagedIds(
  cards: readonly { id: string }[],
  messages: readonly { stagedActions?: readonly { id: string }[] }[],
  states: Readonly<Record<string, string>>,
  revealed: ReadonlySet<string>,
): string[] {
  const inThread = new Set(messages.flatMap(m => (m.stagedActions ?? []).map(sa => sa.id)));
  return cards
    .map(c => c.id)
    .filter(id => inThread.has(id) && !revealed.has(id) && (states[id] ?? 'idle') === 'idle');
}
